import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, RotateCcw, Gauge } from "lucide-react";

const QUESTIONS = [
  {
    q: "Where are you in your cabin crew journey?",
    answers: [
      { label: "Just dreaming — haven't applied yet", scores: { training_support: 1, interview_ready: 1 }, readiness: 0 },
      { label: "Applied, or my assessment day is booked", scores: { interview_ready: 3 }, readiness: 1 },
      { label: "I'm in ground school or training right now", scores: { training_support: 3 }, readiness: 1 },
      { label: "I'm already crew and aiming for promotion", scores: { cabin_manager: 3 }, readiness: 2 },
    ],
  },
  {
    q: "How confident are you answering behavioural questions like 'Tell me about a time when...'?",
    answers: [
      { label: "Honestly, not confident at all", scores: { interview_ready: 2 }, readiness: 0 },
      { label: "Somewhat — I tend to ramble", scores: { interview_ready: 1 }, readiness: 1 },
      { label: "Very confident", scores: {}, readiness: 2 },
    ],
  },
  {
    q: "Have you been through an airline assessment day or group exercise before?",
    answers: [
      { label: "No — I don't know what to expect", scores: { interview_ready: 2 }, readiness: 0 },
      { label: "Yes, once", scores: { interview_ready: 1 }, readiness: 1 },
      { label: "Yes, several times", scores: {}, readiness: 2 },
    ],
  },
  {
    q: "How do you feel about safety & emergency procedures (SEP) exams?",
    answers: [
      { label: "Overwhelmed just thinking about them", scores: { training_support: 2 }, readiness: 0 },
      { label: "Okay, but I'd love study support", scores: { training_support: 1 }, readiness: 1 },
      { label: "Confident — I've got this", scores: {}, readiness: 2 },
    ],
  },
  {
    q: "Is leading a crew as a cabin manager your next goal?",
    answers: [
      { label: "Yes — promotion is my goal", scores: { cabin_manager: 2 }, readiness: 1 },
      { label: "Maybe one day", scores: { cabin_manager: 1 }, readiness: 1 },
      { label: "Not right now", scores: {}, readiness: 1 },
    ],
  },
];

const RESULTS = {
  interview_ready: {
    name: "Interview Ready — 1:1 Coaching",
    duration: "60 min",
    price: "$149 AUD",
    blurb: "Your priority is the assessment day itself. Tony will run you through a real mock interview, show you exactly what assessors score, and polish your answers until they land.",
  },
  training_support: {
    name: "Ground School & Training Support",
    duration: "60 min",
    price: "$99 AUD",
    blurb: "You're headed into training. Tony will mentor you through ground school — SEP, exams and study techniques — so you pass every assessment first time.",
  },
  cabin_manager: {
    name: "Cabin Manager Leadership Mentoring",
    duration: "60 min",
    price: "$99 AUD",
    blurb: "You're ready to lead. Tony will coach your leadership style, escalation handling and promotion interview technique so you step up with confidence.",
  },
};

const PRIORITY = ["interview_ready", "training_support", "cabin_manager"];

export default function ReadinessQuiz() {
  const [step, setStep] = useState(0);
  const [scores, setScores] = useState({ interview_ready: 0, training_support: 0, cabin_manager: 0 });
  const [readiness, setReadiness] = useState(0);
  const done = step >= QUESTIONS.length;

  const answer = (a) => {
    const next = { ...scores };
    Object.entries(a.scores).forEach(([k, v]) => { next[k] += v; });
    setScores(next);
    setReadiness(readiness + a.readiness);
    setStep(step + 1);
  };

  const reset = () => {
    setStep(0);
    setScores({ interview_ready: 0, training_support: 0, cabin_manager: 0 });
    setReadiness(0);
  };

  const winner = PRIORITY.reduce((best, key) => (scores[key] > scores[best] ? key : best), PRIORITY[0]);
  const result = RESULTS[winner];
  const readinessPct = Math.round((readiness / 10) * 100);

  return (
    <section id="quiz" className="py-24 sm:py-32" data-testid="quiz-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs uppercase tracking-[0.25em] font-semibold text-sky-700">60-second check</span>
          <h2 data-testid="quiz-heading" className="text-3xl sm:text-4xl font-bold tracking-tight mt-3 text-[#0A192F]">
            Are you interview ready?
          </h2>
          <p className="text-slate-500 mt-3 text-sm">Answer five quick questions and we'll point you to the session that fits where you are right now.</p>
        </div>

        <div className="mt-12 rounded-2xl bg-white border border-slate-200 shadow-xl overflow-hidden">
          <div className="navy-panel px-8 py-4 flex items-center justify-between text-white">
            <span className="text-xs uppercase tracking-[0.25em] font-semibold text-sky-400 flex items-center gap-2">
              <Gauge className="w-4 h-4" /> Readiness Check
            </span>
            <span data-testid="quiz-progress" className="text-xs text-slate-300 font-semibold">
              {done ? "Complete" : `Question ${step + 1} of ${QUESTIONS.length}`}
            </span>
          </div>
          <div className="h-1 bg-slate-100">
            <div
              data-testid="quiz-progress-bar"
              className="h-full bg-[#F59E0B] transition-[width] duration-500"
              style={{ width: `${(Math.min(step, QUESTIONS.length) / QUESTIONS.length) * 100}%` }}
            />
          </div>

          <div className="p-8 sm:p-10 min-h-[320px]">
            <AnimatePresence mode="wait">
              {!done ? (
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 32 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -32 }}
                  transition={{ duration: 0.3 }}
                >
                  <h3 data-testid="quiz-question" className="text-xl sm:text-2xl font-semibold text-[#0A192F]">
                    {QUESTIONS[step].q}
                  </h3>
                  <div className="mt-6 grid gap-3">
                    {QUESTIONS[step].answers.map((a, i) => (
                      <button
                        key={a.label}
                        data-testid={`quiz-answer-${step}-${i}`}
                        onClick={() => answer(a)}
                        className="text-left rounded-xl border-2 border-slate-200 px-5 py-4 text-sm font-medium text-slate-700 hover:border-sky-500 hover:bg-sky-50 transition-[border-color,background-color] duration-200 flex items-center justify-between group"
                      >
                        {a.label}
                        <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-sky-600 transition-colors duration-200" />
                      </button>
                    ))}
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="result"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4 }}
                  data-testid="quiz-result"
                >
                  <div className="flex flex-wrap items-center gap-4">
                    <span className="text-xs uppercase tracking-[0.25em] font-semibold text-sky-700">Your recommended flight path</span>
                    <span data-testid="quiz-readiness-score" className="rounded-full bg-sky-100 text-sky-800 text-xs font-bold px-3 py-1">
                      Interview readiness: {readinessPct}%
                    </span>
                  </div>
                  <h3 data-testid="quiz-result-session" className="font-display uppercase text-3xl sm:text-4xl font-bold text-[#0A192F] mt-3">
                    {result.name}
                  </h3>
                  <div className="flex items-center gap-3 mt-2 text-sm text-slate-500">
                    <span>{result.duration}</span>
                    <span className="w-1 h-1 rounded-full bg-slate-300" />
                    <span className="font-display text-xl font-bold text-[#F59E0B]">{result.price}</span>
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed mt-4 max-w-xl">{result.blurb}</p>
                  <div className="mt-8 flex flex-wrap gap-3">
                    <Link
                      to={`/book?session=${winner}`}
                      data-testid="quiz-book-btn"
                      className="rounded-full bg-[#F59E0B] text-[#0A192F] font-bold px-7 py-3.5 text-sm hover:bg-amber-400 transition-colors duration-200 flex items-center gap-2"
                    >
                      Book This Session <ArrowRight className="w-4 h-4" />
                    </Link>
                    <button
                      data-testid="quiz-retake-btn"
                      onClick={reset}
                      className="rounded-full border border-slate-300 text-slate-600 font-semibold px-7 py-3.5 text-sm hover:border-sky-500 hover:text-sky-700 transition-colors duration-200 flex items-center gap-2"
                    >
                      <RotateCcw className="w-4 h-4" /> Retake Quiz
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
